<template>
  <div>
    <!-------------------------------------->
    <!-- Gateway Provisioning Section -->
    <!-------------------------------------->
    <v-card class='ma-4' outlined>
      <v-card-title>
        Create/Delete Gateway in QxControl
      </v-card-title>
      <v-card-text>
        Use this section when <span style="font-weight: bold;">provisioning</span> a new Cloud or QXO Gateway.
      </v-card-text>
      <v-container>
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-checkbox 
              v-model="isCloud"
              label="Cloud (de-select for QXO)">
            </v-checkbox>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field 
              v-model="serialNumber" 
              label="System Serial Number" class="ma-0 pa-0"
              placeholder="Serial Number, e.g. QF2720ED004F"
              @change="sernumChange"
              v-validate.initial="sernumValidationRules"
              data-vv-name="serialNumber"
              :error-messages="errors.collect('serialNumber')">
            </v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field 
              v-model="salesOrder" 
              label="Sales Order" class="ma-0 pa-0"
              placeholder="Qumulex Sales Order Number"
              v-validate.initial="'required'"
              data-vv-name="salesOrder"
              :error-messages="errors.collect('salesOrder')">
            </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-text-field 
              v-model="customerId"
              label="Integrator Cust Id" class="ma-0 pa-0"
              placeholder="Integrator's Customer Id, e.g. QUM001"
              v-validate.initial="'max:6|min:6'"
              data-vv-name="customerId"
              :error-messages="errors.collect('customerId')"
            >
            </v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field 
              v-model="distId" 
              label="Distributor Cust Id" class="ma-0 pa-0"
              placeholder="Distributor's Customer Id, e.g. QUM001"
              :disabled="!isCloud"
              v-validate.initial="'max:6|min:6'"
              data-vv-name="distId"
              :error-messages="errors.collect('distId')"
            >
            </v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field 
              v-model="gatewayPlaceName" 
              label="Create Gateway at Place Name" class="ma-0 pa-0"
              placeholder="Place Name, typically Customer PO Number"
              v-validate.initial="'required'"
              data-vv-name="gatewayPlaceName"
              :error-messages="errors.collect('gatewayPlaceName')">
            </v-text-field>
          </v-col>
        </v-row>
      </v-container>
      <v-card-actions>
        <div class="flex-grow-1"></div>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <!-- this span allows me to get a tooltip on the button when it's disabled -->
                <span v-on="on">
                  <v-btn :disabled="disableGatewayCreate" color="primary darken-1" class="ma-1" @click="create">Create</v-btn>
                </span>
              </template>
              <span v-if="!hasValidatorErrors && !customerId && !distId">
                At least one of Integrator or Distributor Id must be valid
              </span>
              <span v-else>
                This will: <br>
                1) find the matching Place in the {{targetEnv}} hierarchy by Customer ID (preferring the Integrator) <br> 
                2) create a new Customer Place there with the provided name <br>
                3) create a Gateway at the new customer place with the WG IP and UUID, model number and serial number populated.<br>
                4) add the Sales Order to the system record in the sysprep database and relate this system to Customer ID(s).<br>
              </span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <!-- this span allows me to get a tooltip on the button when it's disabled -->
                <span v-on="on">
                  <v-btn :disabled="errors.has('serialNumber')" color="primary darken-1" class="ma-1" @click="del">Delete</v-btn>
                </span>
              </template>
              <span>
                This removes the Gateway and its Place from the {{targetEnv}} hierarchy <br>
                and unrelates the system from Customer ID(s) in sysprep, <br>
                but does not delete the system from sysprep nor remove the SO number.
              </span>
            </v-tooltip>
        <v-btn color="primary darken-1" class="ma-1" @click="close">Cancel</v-btn>
      </v-card-actions>
    </v-card>
    <!-------------------------------------->
    <!-- License Section -->
    <!-------------------------------------->
    <v-card class='ma-4' outlined>
      <v-card-title>
        Create Gateway License
      </v-card-title>
      <v-card-text>
        Use this section when <span style="font-weight: bold;">provisioning</span> a new Cloud or QXO Gateway. <br>
      </v-card-text>
      <v-container>
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-text-field 
              v-model="serialNumber" 
              label="System Serial Number" class="ma-0 pa-0"
              placeholder="Serial Number, e.g. QF2720ED004F"
              @change="sernumChange"
              v-validate.initial="sernumValidationRules"
              data-vv-name="serialNumber"
              :error-messages="errors.collect('serialNumber')">
            </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="3" md="3">
            <v-text-field 
              v-model="distId" 
              label="Distributor Cust Id" class="ma-0 pa-0"
              placeholder="Distributor's Cust Id, e.g. QUM001">
            </v-text-field>
          </v-col>
          <v-col cols="12" sm="3" md="2">
            <v-text-field 
              v-model="maxResources"
              label="Max Resource Count" class="ma-0 pa-0" 
              type="number"
              :disabled="licenseVersion < 1"
              @change="clearLicense"
              @click="clearLicense"
              v-validate.initial="'required|min_value:1|max_value:128'"
              data-vv-name="maxResources"
              :error-messages="errors.collect('maxResources')">
            </v-text-field>
          </v-col>
          <v-col cols="12" sm="3" md="2">
            <v-text-field 
              v-model="resourceCount"
              label="Licensed Resource Count" class="ma-0 pa-0" 
              type="number"
              @change="clearLicense"
              @click="clearLicense"
              v-validate.initial="'required|min_value:1|max_value:128'"
              data-vv-name="resourceCount"
              :error-messages="errors.collect('resourceCount')">
            </v-text-field>
          </v-col>
          <v-col cols="12" sm="12" md="2">
            <v-menu
              ref="menu"
              v-model="menu"
              class="ma-0 pa-0"
              :disabled="licenseVersion < 1"
              :close-on-content-click="false"
              :return-value.sync="expDate"
              transition="scale-transition"
              offset-y
              min-width="290px">
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="expDate"
                  :disabled="licenseVersion < 1"
                  class="ma-0 pa-0"
                  v-validate.initial="'required|date_format:yyyy-MM-dd'"
                  data-vv-name="expDate"
                  :error-messages="errors.collect('expDate')"
                  label="Expiration Date"
                  placeholder="Exp Date (yyyy-MM-dd)"
                  prepend-icon="event"
                  :readonly="false"
                  v-on="on">
                </v-text-field>
              </template>
              <v-date-picker v-model="expDate" no-title scrollable>
              <v-spacer></v-spacer>
              <v-btn text color="primary" @click="menu = false; validateDate()">Cancel</v-btn>
              <v-btn text color="primary" @click="$refs.menu.save(expDate); validateDate()">OK</v-btn>
              </v-date-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="3" md="1">
            <v-radio-group
              v-model="licenseVersion"
              label="License Version" class="ma-0 pa-0"
              dense
            >
              <v-radio
                v-for="n in licenseVersions.length"
                :key="n-1"
                :label="licenseVersions[n-1]"
              >
              </v-radio>
            </v-radio-group>
          </v-col>
          <v-col cols="12" sm="3" md="2">
            <v-radio-group
              v-model="licenseType"
              label="License Type" class="ma-0 pa-0"
              dense
              :disabled="licenseVersion < 1"
              v-validate.initial="'required|min_value:0|max_value:2'"
              data-vv-name="licenseType"
              :error-messages="errors.collect('licenseType')"
            >
              <v-radio
                v-for="n in licenseTypes.length"
                :key="n-1"
                :label="licenseTypes[n-1]"
              >
              </v-radio>
            </v-radio-group>
          </v-col>
          <v-col cols="12" sm="3" md="2">
            <v-text-field 
              v-model="maxCameras"
              label="Max Camera Count" class="ma-0 pa-0" 
              type="number"
              :disabled="licenseVersion < 1"
              @change="clearLicense"
              @click="clearLicense"
              v-validate.initial="'required|min_value:1|max_value:128'"
              data-vv-name="maxCameras"
              :error-messages="errors.collect('maxCameras')">
            </v-text-field>
          </v-col>
          <v-col cols="12" sm="3" md="2">
            <v-text-field 
              v-model="cameraCount"
              label="Licensed Camera Count" class="ma-0 pa-0" 
              type="number"
              :disabled="licenseVersion < 1"
              @change="clearLicense"
              @click="clearLicense"
              v-validate.initial="'required|min_value:1|max_value:128'"
              data-vv-name="cameraCount"
              :error-messages="errors.collect('cameraCount')">
            </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12" sm="3" md="10">
            <v-textarea 
              v-model="license"
              outlined auto-grow dense
              rows=1
              placeholder="license"
              v-validate="'min:20|max:2000'"
              data-vv-name="license"
              :error-messages="errors.collect('license')">
            </v-textarea>
          </v-col>
        </v-row>
      </v-container>
      <v-card-actions>
        <div class="flex-grow-1"></div>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <!-- this span allows me to get a tooltip on the button when it's disabled -->
                <span v-on="on">
                  <v-btn :disabled="disableLicenseCreate" color="primary darken-1" class="ma-1" @click="createLic">Create</v-btn>
                </span>
              </template>
              <span>
                This will create a license for {{targetEnv}} and save that license in sysprep components for the system.<br>
              </span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <!-- this span allows me to get a tooltip on the button when it's disabled -->
                <span v-on="on">
                  <v-btn :disabled="errors.has('serialNumber') || !license || errors.has('license')" color="primary darken-1" class="ma-1" @click="applyLic">Apply</v-btn>
                </span>
              </template>
              <span>
                This will apply the license to the Gateway.<br>
              </span>
            </v-tooltip>
        <v-btn color="primary darken-1" class="ma-1" @click="closeLic">Cancel</v-btn>
      </v-card-actions>
    </v-card>
    <!-------------------------------------->
    <!-- Replacement Section -->
    <!-------------------------------------->
    <v-card class='ma-4' outlined>
      <v-card-title>
        Gateway RMA/Replacement
      </v-card-title>
      <v-card-subtitle>
        Coming Soon!
      </v-card-subtitle>
      <v-container>
        <v-row>
          <v-col cols="12" sm="6" md="3">
            <v-text-field v-model="oldSerialNumber" 
              label="Old System Serial Number" class="ma-0 pa-0"
              placeholder="Old Serial Number, e.g. QF2720ED004F"
              :disabled="true">
            </v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field v-model="newSerialNumber"
              label="New System Serial Number" class="ma-0 pa-0"
              placeholder="New Serial Number, e.g. QF2720ED004F"
              :disabled="true">
            </v-text-field>
          </v-col>
        </v-row>
      </v-container>
      <v-card-actions>
        <div class="flex-grow-1"></div>
        <v-btn :disabled="true" color="primary darken-1" @click="swap">Swap</v-btn>
        <v-btn :disabled="true" color="primary darken-1" @click="closeRepl">Cancel</v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<script>
import env from '../../server.config'

export default {
  name: "Gateway",
  props: {
    app: Object,
    systemId: String,
  },
  data: () => ({
    menu: false,
    serialNumber: '',
    lastSerialNumber: '',
    salesOrder: '',
    customerId: '',
    distId: '',
    gatewayPlaceName: '',
    resourceCount: 0,
    cameraCount: 0,
    maxResources: 0,
    maxCameras: 0,
    licenseType: 'QXP',
    licenseTypes: ['QXO', 'QXP', 'QXC'],
    licenseVersion: 2,
    licenseVersions: ['1', '2'],
    expDate: '',
    oldSerialNumber: '',
    newSerialNumber: '',
    system: {},
    license: '',
    isCloud: true,
  }),

  computed: {
    disableGatewayCreate() {
      if (!this.customerId && !this.distId) return true; // At least one customerId must be filled in
      return this.hasValidatorErrors;
    },
    disableLicenseCreate() {
      if (this.licenseVersion < 1) {
        return this.errors.has('serialNumber') ||
          this.errors.has('resourceCount'); // v1 licenses only have resource count field
      } else {
        return this.errors.has('serialNumber') ||
          this.errors.has('maxResources') ||
          this.errors.has('resourceCount') ||
          this.errors.has('maxCameras') ||
          this.errors.has('cameraCount') ||
          this.errors.has('expData') ||
          this.errors.has('licenseType');
      }
    },
    hasValidatorErrors() {
      return  this.$validator.errors.has('serialNumber')  || 
        this.$validator.errors.has('salesOrder') || 
        this.$validator.errors.has('customerId') || 
        this.$validator.errors.has('distId') || 
        this.$validator.errors.has('gatewayPlaceName');
    },
    isSerialNumberValid() {
      return !this.$validator.errors.has('serialNumber');
    },
    sernumValidationRules() {
      if (env.prodLevel == "develop" || env.prodLevel == "unstable") {
        return 'required|max:17|min:3';
      } else {
        return 'required|max:17|min:12';
      }
    },
    targetEnv() {
      if (this.isCloud) {
        return env.prodLevel
      } else {
        return this.serialNumber == '' ? 'the local gateway' : this.serialNumber;
      }
    },
  },

  mounted() {
    console.log('gateway.vue created.  Prod level: ', env.prodLevel)
    this.initialize();
  },

  methods: {
    initialize() {
      if (this.systemId !== '') { // serial number MAY be passed by path, or could be empty
        this.serialNumber = this.systemId;
        this.getSystem(this.serialNumber)
      }
      // Initialize expiration date to five years from now, plus a month grace period
      const exp = new Date();
      exp.setMonth(exp.getMonth()+61);
      this.expDate = exp.toISOString().substring(0,10);
    },

    create() {
      this.postGateway()
    },

    del() {
      let msg = "Are you sure you want to delete Gateway with Serial Number: " + this.serialNumber + "?\r\n"
      msg += "\r\n"
      msg += "NOTE: This will not delete the System from the Systems database, "
      msg += "just delete the Gateway and Place corresponding to this System from QxControl "
      msg += "and unrelate " + this.serialNumber + " from all integrators and distributors.\r\n"
      msg += "\r\n"
      msg += "NOTE: If this gateway has cloud recording, it will become unsearchable.  "
      msg += "If the gateway is deleted, the cloud video will expire according to the subscription period at the time of its recording.\r\n"
      msg += "You can check for video by getting cloud stats for cameras on this gateway from the Health->Camera page."

      if (confirm(msg)) {
        this.deleteGateway()
      }
    },

    close() {
      this.serialNumber = ''
      this.lastSerialNumber = ""
      this.customerId = ''
      this.distId = ''
      this.gatewayPlaceName = ''
      this.salesOrder = ''
      this.system = {}
    },

    createLic() {
      this.postLicenseCreate()
    },

    applyLic() {
      this.postLicenseApply()
    },

    closeLic() {
      this.serialNumber = ''
      this.resourceCount = 0
      this.clearLicense()
    },

    swap() {
    },

    closeRepl() {
    },

    sernumChange() {
      console.log("sernumchange")
      if (this.isSerialNumberValid && this.serialNumber !== this.lastSerialNumber) {
        this.lastSerialNumber = this.serialNumber // I can get duplicate events on @change for keydown and enter
        this.getSystem(this.serialNumber)
      }
    },

    clearLicense() {
      this.license = '';
    },

    getSystem(sernum) {
      let url = '/API/public/system/' + sernum
      console.log('getSystem(): url=' + url)
      this.app.restApi({
          method: 'get',
          url: url,
          headers: {
            'Authorization': 'Bearer ' + this.app.userProfile.token
          }
        })
        .then(response => {
          this.system = response.data;
          console.log("system = " + this.system.id + this.system.salesOrder)
          this.salesOrder = this.system.salesOrder
          console.log("Sales Order = " + this.salesOrder)
          this.getMaxLicenseValues(this.system.model)
        })
        .catch(error => {
          console.log(error);
          if (error.response.status === 404) {
            alert("System with serial number " + this.serialNumber + " not found.")
          } else {
            let msg = "Error getting System for serial number " + this.serialNumber;
            msg += ". (" + error.response.status + ")"
            alert(msg)
          }
          this.system = {};
          this.salesOrder = ''
          this.serialNumber = ''
        });
    },

    getMaxLicenseValues(model) {
      switch (model.substring(0,6)) {
        case 'QXL110':
        case 'QXL120':
        case 'QXS100':
        case 'QXS300':
          this.maxCameras = 16;
          this.maxResources = 32;
        break;
        case 'QXL200':
        case 'QXS500':
          if (model.indexOf('-UP') >= 0) {
            this.maxCameras = 64;
            this.maxResources = 96;
          } else {
            this.maxCameras = 32;
            this.maxResources = 64;
          }
        break;
        case 'QXP710':
        case 'QXP720':
          this.maxCameras = 96;
          this.maxResources = 96;
        break;
        default:
          this.maxCameras = 8;
          this.maxResources = 8;
      }
    },

    updateSystem(id) {
      // Add the sales order to the system record
      let url = '/API/public/system/' + id
      url += "?salesOrder=" + this.salesOrder
      console.log('updateSystem(): url=' + url)
      this.app.restApi({
          method: 'post',
          url: url,
          headers: {
            'Authorization': 'Bearer ' + this.app.userProfile.token
          }
        })
        .then(response => {
          console.log(response);
          this.system = response.data;
          this.salesOrder = this.system.salesOrder
        })
        .catch(error => {
          console.log(error);
          this.salesOrder = ''
          let msg = "Error updating System serial number " + this.serialNumber;
          msg += ". (" + error.response.status + ")"
          alert(msg)
        });
    },

    relateSystem(systemId, customerId, isDist) {
      // Relate system to customer
      let urla = `/API/public/${isDist ? 'distributor':'integrator'}/${customerId}/system/${systemId}`
      console.log('relateSystem(): url=' + urla)
      this.app.restApi({
          method: 'post',
          url: urla,
          headers: {
            'Authorization': 'Bearer ' + this.app.userProfile.token
          }
        })
        .then(response => {
          console.log(response);
        })
        .catch(error => {
          console.log(error);
          let msg = "Error relating System serial number " + this.serialNumber
          msg += " to " + customerId
          msg += ". (" + error.response.status + ")"
          alert(msg)
        });
    },

    unrelateSystem(systemId, isDist) {
      // Relate system to customer
      let urla = `/API/public/${isDist ? 'distributor':'integrator'}/system/${systemId}`
      console.log('unrelateSystem(): url=' + urla)
      this.app.restApi({
          method: 'delete',
          url: urla,
          headers: {
            'Authorization': 'Bearer ' + this.app.userProfile.token
          }
        })
        .then(response => {
          console.log(response);
        })
        .catch(error => {
          console.log(error);
          let msg = "Error unrelating System serial number " + this.serialNumber
          msg += ". (" + error.response.status + ")"
          alert(msg)
        });
    },

    postGateway() {
      let url = '/API/public/system/'
      url += this.serialNumber + '/gw/new?'
      url += `customerId=${this.customerId ? this.customerId : this.distId}`  // prefer Integrator's ID if both specified
      url += '&placeName=' + encodeURIComponent(this.gatewayPlaceName)
      url += '&isCloud=' + this.isCloud
      console.log('postGateway(): url=' + url)
      this.app.restApi({
          method: 'post',
          url: url,
          headers: {
            'Authorization': 'Bearer ' + this.app.userProfile.token
          }
        })
        .then(response => {
          console.log(response);
          // Only update system sales order and relate system to customer if hierarchy create succeeds
          this.updateSystem(this.system.id)
          if (this.customerId) this.relateSystem(this.system.id, this.customerId, false)
          if (this.distId) this.relateSystem(this.system.id, this.distId, true)
        })
        .catch(error => {
          console.log(error);
          if (error.response.status === 409) {
            alert("Gateway for serial number " + this.serialNumber + " already exists.")
          } else {
            let msg = "Gateway could not be created for serial number " + this.serialNumber;
            msg += ` for CustomerID = ${this.customerId ? this.customerId : this.distId}.  `
            msg += "Does an Integrator with that CustomerID exist in QxControl?  "
            msg += "If yes, check Portal API log files for error details. (" + error.response.status + ")"
            alert(msg)
          }
        });
    },

    deleteGateway() {
      let url = '/API/public/system/'
      url += this.serialNumber + '/gw?'
      url += 'deletePlace=' + true
      url += '&isCloud=' + this.isCloud
      console.log('deleteGateway(): url=' + url)
      this.app.restApi({
          method: 'delete',
          url: url,
          headers: {
            'Authorization': 'Bearer ' + this.app.userProfile.token
          }
        })
        .then(response => {
          console.log("deleteGateway() response: ", response);
        })
        .catch(error => {
          console.log("deleteGateway() error: ", error);
          let msg = "";
          if (error.response.status === 404) {
            msg = "Gateway for serial number " + this.serialNumber + " was not found in QxControl. "
          } else {
            msg = "Gateway serial number " + this.serialNumber + " could not be deleted in QxControl. ";
            msg += "Check Portal API log files for error details.(" + error.response.status + ") "
          }
          msg += "Portal will still attempt to unrelate gateway from any integrators or distributors."
          alert(msg)
        })
        .finally(() => {
          this.unrelateSystem(this.system.id, false)
          this.unrelateSystem(this.system.id, true)
          this.close()
        });
    },

    postLicenseCreate() {
      let url = '/API/public/system/'
      url += this.serialNumber + '/license/new?'
      url += 'resourceCount=' + this.resourceCount
      if (this.licenseVersion >= 0) url += "&version=" + this.licenseVersions[this.licenseVersion]
      if (this.licenseType >= 0) url += "&type=" + this.licenseTypes[this.licenseType]
      if (this.licenseVersion > 0) url += "&cameraCount=" + this.cameraCount
      if (this.licenseVersion > 0) url += "&maxResources=" + this.maxResources
      if (this.licenseVersion > 0) url += "&maxCameras=" + this.maxCameras
      if (this.licenseVersion > 0) url += "&exp=" + Date.parse(this.expDate)/1000
      console.log('postLicenseCreate(): url=' + url)
      this.app.restApi({
          method: 'post',
          url: url,
          headers: {
            'Authorization': 'Bearer ' + this.app.userProfile.token
          }
        })
        .then(response => {
          const updatedLicense = Object.values(response.data);
          if (this.distId) {
            this.relateLicense(updatedLicense[0].id, this.distId);
          }
          this.license = updatedLicense[0].jwt;
        })
        .catch(error => {
          console.log(error);
          if (error.response.status === 404) {
            alert("Gateway for serial number " + this.serialNumber + " not found.")
          } else if (error.response.status === 409) {
            alert("A license for " + this.serialNumber + " exists.  Please edit or delete it from the licenses page.")
          } else {
            let msg = "License could not be created for serial number " + this.serialNumber;
            msg += "Check Portal API log files for error details. (" + error.response.status + ")"
            alert(msg)
          }
        });
    },

    relateLicense(id, distId) {
      this.app.restApi({
          method: 'post',
          url: `/API/public/distributor/${distId}/license/${id}`,
          headers: {
            'Authorization': 'Bearer ' + this.app.userProfile.token
          }
        })
        .then()
        .catch(function (error) {
          console.log(error);
          alert("Relate License to " + distId + " failed (" + error.response.status + ")")
        });
    },

    postLicenseApply() {
      let url = '/API/public/system/'
      url += this.serialNumber + '/gw?'
      url += 'isCloud=' + 'false'
      url += '&licenseJwt=' + this.license
      console.log('postLicenseApply(): url=' + url)
      this.app.restApi({
          method: 'post',
          url: url,
          headers: {
            'Authorization': 'Bearer ' + this.app.userProfile.token
          }
        })
        .then(response => {
          console.log(response);
        })
        .catch(error => {
          console.log(error);
          if (error.response.status === 404) {
            alert("Gateway for serial number " + this.serialNumber + " not found.")
          } else {
            let msg = "License could not be applied for serial number " + this.serialNumber;
            msg += "Check Portal API log files for error details. (" + error.response.status + ")"
            alert(msg)
          }
        });
    },

    validateDate() {
      // need this to trigger validator after date picker for close date sets close date text input
      this.$validator.validate('expDate');
    },

  }
}
</script>